import React, { createContext, useContext } from 'react';

interface WorkerContextType {
  worker: {
    getPartnerSuggestions: (filters: {
      opportunity_owner__manager?: string;
      opportunity_owner?: string;
      fiscal_period?: string;
      stages?: string[];
      partners?: string[];
    }) => Promise<any>;
    getScoreboard: () => Promise<any>;
    getPartnerSuggestionFilters: () => Promise<{
      managers: string[];
      owners: string[];
      periods: string[];
      stages: string[];
      partners: string[];
    }>;
  };
}

const WorkerContext = createContext<WorkerContextType | undefined>(undefined);

export function useWorkerContext() {
  const context = useContext(WorkerContext);
  if (!context) {
    throw new Error('useWorkerContext must be used within a WorkerContextProvider');
  }
  return context;
}

interface WorkerProviderProps {
  children: React.ReactNode;
}

export function WorkerContextProvider({ children }: WorkerProviderProps) {
  const worker = {
    getScoreboard: async () => {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/scoreboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch scoreboard data');
      }
      
      return response.json();
    },
    getPartnerSuggestions: async (filters: {
      opportunity_owner__manager?: string;
      opportunity_owner?: string;
      fiscal_period?: string;
      stages?: string[];
      partners?: string[];
    }) => {
      const queryParams = new URLSearchParams();
      if (filters.opportunity_owner__manager) {
        queryParams.set('opportunity_owner__manager', filters.opportunity_owner__manager);
      }
      if (filters.opportunity_owner) {
        queryParams.set('opportunity_owner', filters.opportunity_owner);
      }
      if (filters.fiscal_period) {
        queryParams.set('fiscal_period', filters.fiscal_period);
      }
      if (filters.stages && filters.stages.length > 0) {
        queryParams.set('stages', JSON.stringify(filters.stages));
      }
      if (filters.partners && filters.partners.length > 0) {
        queryParams.set('partners', JSON.stringify(filters.partners));
      }

      const apiUrl = import.meta.env.VITE_API_URL || '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/partner-suggestions?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch partner suggestions');
      }
      return response.json();
    },
    getPartnerSuggestionFilters: async () => {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/partner-suggestion-filters`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch partner suggestion filters');
      }
      return response.json();
    }
  };

  return (
    <WorkerContext.Provider value={{ worker }}>
      {children}
    </WorkerContext.Provider>
  );
}
